import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { CraftingLogSummary } from '../hooks/useCraftingLog';

interface CraftingLogTableProps {
  entries: CraftingLogSummary[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export function CraftingLogTable({ entries, loading, hasMore, onLoadMore }: CraftingLogTableProps) {
  if (loading && entries.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No crafting log entries found.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Time</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Recipe</th>
              <th className="text-left px-4 py-2 font-medium">Inputs</th>
              <th className="text-left px-4 py-2 font-medium">Outputs</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {entries.map((entry) => (
              <tr key={entry.logId} className="hover:bg-muted/30">
                <td className="px-4 py-2 whitespace-nowrap">
                  {new Date(entry.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-2">
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">{entry.recipeType}</span>
                </td>
                <td className="px-4 py-2">{entry.recipeName}</td>
                <td className="px-4 py-2">
                  {entry.inputsConsumed.map((i) => `${i.quantity}x ${i.itemName}`).join(', ')}
                </td>
                <td className="px-4 py-2">
                  {entry.outputsCreated.map((o) => `${o.quantity}x ${o.itemName}`).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="text-center">
          <Button variant="ghost" onClick={onLoadMore} disabled={loading}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
